/**
 * 阴影配置
 * 基于ERC-1155 Multi-Asset Platform的现代化阴影系统
 * 体现多样性、高效率和现代感的视觉层次
 */
// @mui
import { alpha } from '@mui/material/styles';
//
import palette from './palette';

// ----------------------------------------------------------------------

// ERC-1155 Multi-Asset Shadow Colors
const LIGHT_MODE = {
  primary: '#4CAF50', // 绿色
  secondary: '#2196F3', // 蓝色
  multi: '#FF9800', // 橙色
  diverse: '#9C27B0', // 紫色
  info: '#00BCD4',
  success: '#4CAF50',
  warning: '#FFC107',
  error: '#F44336',
  grey: '#637381',
};

const DARK_MODE = {
  primary: '#66BB6A',
  secondary: '#42A5F5',
  multi: '#FFB74D',
  diverse: '#BA68C8',
  info: '#4FC3F7',
  success: '#81C784',
  warning: '#FFD54F',
  error: '#EF5350',
  grey: '#9ca3af',
};

// ERC-1155 现代化阴影生成函数
const createShadow = (color) => {
  const transparent1 = alpha(color, 0.16);
  const transparent2 = alpha(color, 0.12);
  const transparent3 = alpha(color, 0.08);
  return [
    'none',
    `0px 1px 2px 0px ${transparent1}, 0px 1px 3px 1px ${transparent3}`,
    `0px 1px 3px 0px ${transparent1}, 0px 2px 6px 2px ${transparent3}`,
    `0px 2px 4px 0px ${transparent1}, 0px 3px 8px 2px ${transparent3}`,
    `0px 2px 5px 0px ${transparent1}, 0px 4px 10px 3px ${transparent3}`,
    `0px 3px 6px 0px ${transparent1}, 0px 5px 12px 4px ${transparent3}`,
    `0px 3px 7px 0px ${transparent1}, 0px 6px 14px 4px ${transparent3}`,
    `0px 4px 8px 0px ${transparent1}, 0px 7px 16px 5px ${transparent3}`,
    `0px 4px 9px 0px ${transparent1}, 0px 8px 18px 6px ${transparent3}`,
    `0px 5px 10px 0px ${transparent1}, 0px 9px 20px 6px ${transparent3}`,
    `0px 5px 11px 0px ${transparent1}, 0px 10px 22px 7px ${transparent3}`,
    `0px 6px 12px 0px ${transparent1}, 0px 11px 24px 8px ${transparent3}`,
    `0px 6px 13px 0px ${transparent1}, 0px 12px 26px 8px ${transparent3}`,
    `0px 7px 14px 0px ${transparent1}, 0px 13px 28px 9px ${transparent3}`,
    `0px 7px 15px 0px ${transparent1}, 0px 14px 30px 10px ${transparent3}`,
    `0px 8px 16px 0px ${transparent1}, 0px 15px 32px 10px ${transparent3}`,
    `0px 8px 17px 0px ${transparent1}, 0px 16px 34px 11px ${transparent3}`,
    `0px 9px 18px 0px ${transparent1}, 0px 17px 36px 12px ${transparent3}`,
    `0px 9px 19px 0px ${transparent1}, 0px 18px 38px 12px ${transparent3}`,
    `0px 10px 20px 0px ${transparent1}, 0px 19px 40px 13px ${transparent3}`,
    `0px 10px 21px 0px ${transparent1}, 0px 20px 42px 14px ${transparent3}`,
    `0px 11px 22px 0px ${transparent1}, 0px 21px 44px 14px ${transparent3}`,
    `0px 11px 23px 0px ${transparent1}, 0px 22px 46px 15px ${transparent3}`,
    `0px 12px 24px 0px ${transparent1}, 0px 23px 48px 16px ${transparent3}`,
    `0px 12px 25px 0px ${transparent1}, 0px 24px 50px 16px ${transparent3}`,
  ];
};

// ERC-1155 多彩阴影生成函数
const createColoredShadow = (color, intensity = 0.3) => {
  const shadowColor = alpha(color, intensity);
  return `0px 4px 20px ${shadowColor}, 0px 2px 8px ${alpha(color, intensity * 0.5)}`;
};

// ERC-1155 多资产阴影生成函数
const createMultiAssetShadow = (colors, intensity = 0.2) => {
  return colors.map((color, index) => {
    const offset = index * 2;
    return `${offset}px ${offset + 2}px ${8 + offset * 2}px ${alpha(color, intensity)}`;
  }).join(', ');
};

// ERC-1155 Multi-Asset Shadow Presets
const MULTI_ASSET_SHADOWS = {
  card: {
    light: '0px 4px 20px rgba(34, 197, 94, 0.12), 0px 2px 8px rgba(59, 130, 246, 0.08)',
    dark: '0px 4px 20px rgba(34, 197, 94, 0.2), 0px 2px 8px rgba(59, 130, 246, 0.15)'
  },
  button: {
    light: '0px 2px 8px rgba(249, 115, 22, 0.15), 0px 1px 4px rgba(168, 85, 247, 0.1)',
    dark: '0px 2px 8px rgba(249, 115, 22, 0.25), 0px 1px 4px rgba(168, 85, 247, 0.2)'
  },
  modal: {
    light: '0px 8px 32px rgba(0, 0, 0, 0.1), 0px 4px 16px rgba(34, 197, 94, 0.06)',
    dark: '0px 8px 32px rgba(0, 0, 0, 0.2), 0px 4px 16px rgba(34, 197, 94, 0.12)'
  },
  tooltip: {
    light: '0px 2px 8px rgba(0, 0, 0, 0.12), 0px 1px 4px rgba(59, 130, 246, 0.08)',
    dark: '0px 2px 8px rgba(0, 0, 0, 0.2), 0px 1px 4px rgba(59, 130, 246, 0.15)'
  },
  batchOperation: {
    light: '0px 6px 24px rgba(249, 115, 22, 0.15), 0px 3px 12px rgba(168, 85, 247, 0.1)',
    dark: '0px 6px 24px rgba(249, 115, 22, 0.25), 0px 3px 12px rgba(168, 85, 247, 0.2)'
  },
  multiSelect: {
    light: createMultiAssetShadow(['#22c55e', '#3b82f6', '#f97316'], 0.1),
    dark: createMultiAssetShadow(['#22c55e', '#3b82f6', '#f97316'], 0.18)
  }
};

// Legacy ERC-721 Premium Shadow Presets (保持兼容性)
const PREMIUM_SHADOWS = {
  card: {
    light: '0px 4px 20px rgba(212, 175, 55, 0.15), 0px 2px 8px rgba(212, 175, 55, 0.1)',
    dark: '0px 4px 20px rgba(212, 175, 55, 0.25), 0px 2px 8px rgba(212, 175, 55, 0.15)'
  },
  button: {
    light: '0px 2px 8px rgba(147, 51, 234, 0.2), 0px 1px 4px rgba(147, 51, 234, 0.1)',
    dark: '0px 2px 8px rgba(147, 51, 234, 0.3), 0px 1px 4px rgba(147, 51, 234, 0.2)'
  },
  modal: {
    light: '0px 8px 32px rgba(0, 0, 0, 0.12), 0px 4px 16px rgba(212, 175, 55, 0.08)',
    dark: '0px 8px 32px rgba(0, 0, 0, 0.24), 0px 4px 16px rgba(212, 175, 55, 0.16)'
  },
  tooltip: {
    light: '0px 2px 8px rgba(0, 0, 0, 0.15), 0px 1px 4px rgba(147, 51, 234, 0.1)',
    dark: '0px 2px 8px rgba(0, 0, 0, 0.25), 0px 1px 4px rgba(147, 51, 234, 0.2)'
  }
};

const createCustomShadow = (color) => {
  const transparent = alpha(color, 0.16);
  return {
    // 基础阴影 - ERC-721 高端风格
    basic: {
      // 轻微阴影 - 精致感
      light: '0 2px 4px rgba(0, 102, 255, 0.08), 0 1px 2px rgba(138, 43, 226, 0.06)',
      // 标准阴影 - 层次感
      medium: '0 4px 8px rgba(0, 102, 255, 0.12), 0 2px 4px rgba(138, 43, 226, 0.08)',
      // 深度阴影 - 高端感
      heavy: '0 8px 16px rgba(0, 102, 255, 0.16), 0 4px 8px rgba(138, 43, 226, 0.12)',
      // 极深阴影 - 奢华感
      extreme: '0 16px 32px rgba(0, 102, 255, 0.20), 0 8px 16px rgba(138, 43, 226, 0.16)',
    },
    // ERC-721 专属彩色阴影
    colored: {
      // 主色阴影 - 蓝色科技感
      primary: '0 6px 20px 0 rgba(0, 102, 255, 0.35)',
      // 次色阴影 - 紫色神秘感
      secondary: '0 6px 20px 0 rgba(138, 43, 226, 0.35)',
      // 金色阴影 - 奢华感
      gold: '0 6px 20px 0 rgba(255, 215, 0, 0.40)',
      // 成功色阴影 - 绿色增值感
      success: '0 6px 20px 0 rgba(76, 175, 80, 0.35)',
      // 警告色阴影 - 橙色注意感
      warning: '0 6px 20px 0 rgba(255, 152, 0, 0.35)',
      // 错误色阴影 - 红色警示感
      error: '0 6px 20px 0 rgba(244, 67, 54, 0.35)',
      // 信息色阴影 - 蓝色信息感
      info: '0 6px 20px 0 rgba(33, 150, 243, 0.35)',
      // 稀有度阴影 - 彩虹渐变
      rare: '0 8px 24px 0 rgba(255, 215, 0, 0.25), 0 4px 12px 0 rgba(138, 43, 226, 0.25)',
    },
    z1: `0 1px 2px 0 ${transparent}`,
    z8: `0 8px 16px 0 ${transparent}`,
    z12: `0 12px 24px -4px ${transparent}`,
    z16: `0 16px 32px -4px ${transparent}`,
    z20: `0 20px 40px -4px ${transparent}`,
    z24: `0 24px 48px 0 ${transparent}`,
    // ERC-721 专属阴影效果
    nftCard: `0 4px 12px 0 ${alpha(color, 0.15)}, 0 16px 32px -4px ${alpha(color, 0.10)}`,
    premiumCard: `0 8px 24px 0 ${alpha(color, 0.20)}, 0 20px 40px -4px ${alpha(color, 0.15)}`,
    luxuryCard: `0 12px 36px 0 ${alpha(color, 0.25)}, 0 24px 48px -4px ${alpha(color, 0.20)}`,
    rareGlow: `0 0 20px ${alpha('#FFD700', 0.4)}, 0 0 40px ${alpha('#8A2BE2', 0.2)}`,
    
    // 原有阴影效果
    card: `0 0 2px 0 ${alpha(color, 0.2)}, 0 12px 24px -4px ${alpha(color, 0.12)}`,
    dialog: `-40px 40px 80px -8px ${alpha(palette.light.common.black, 0.24)}`,
    dropdown: `0 0 2px 0 ${alpha(color, 0.24)}, -20px 20px 40px -4px ${alpha(color, 0.24)}`,
  };
};

export const customShadows = {
  light: createCustomShadow(LIGHT_MODE.primary),
  // ERC-721 暗色模式高端阴影
  dark: {
    z1: '0 1px 2px 0 rgba(0, 0, 0, 0.48)',
    z8: '0 8px 16px 0 rgba(0, 0, 0, 0.48)',
    z12: '0 12px 24px -4px rgba(0, 0, 0, 0.48)',
    z16: '0 16px 32px -4px rgba(0, 0, 0, 0.48)',
    z20: '0 20px 40px -4px rgba(0, 0, 0, 0.48)',
    z24: '0 24px 48px 0 rgba(0, 0, 0, 0.48)',
    
    // ERC-721 专属暗色阴影
    primary: `0 8px 16px 0 ${alpha('#000000', 0.48)}`,
    secondary: `0 8px 16px 0 ${alpha('#000000', 0.48)}`,
    gold: '0 8px 20px 0 rgba(255, 215, 0, 0.35)',
    premium: '0 12px 28px 0 rgba(0, 102, 255, 0.25), 0 6px 14px 0 rgba(138, 43, 226, 0.20)',
    luxury: '0 16px 36px 0 rgba(255, 215, 0, 0.30), 0 8px 18px 0 rgba(138, 43, 226, 0.25)',
    rare: '0 0 24px rgba(255, 215, 0, 0.45), 0 0 48px rgba(138, 43, 226, 0.25)',
    
    // NFT 卡片专属阴影
    nftCard: '0 4px 16px 0 rgba(0, 0, 0, 0.25), 0 16px 36px -4px rgba(0, 102, 255, 0.15)',
    premiumCard: '0 8px 28px 0 rgba(0, 0, 0, 0.30), 0 20px 44px -4px rgba(138, 43, 226, 0.20)',
    luxuryCard: '0 12px 40px 0 rgba(0, 0, 0, 0.35), 0 24px 52px -4px rgba(255, 215, 0, 0.25)',
    
    // 原有阴影效果
    card: '0 0 2px 0 rgba(0, 0, 0, 0.2), 0 12px 24px -4px rgba(0, 0, 0, 0.12)',
    dropdown: '0 0 2px 0 rgba(0, 0, 0, 0.24), -20px 20px 40px -4px rgba(0, 0, 0, 0.24)',
    
    // 基础阴影 - ERC-721 高端风格
    basic: {
      // 轻微阴影 - 精致感
      light: '0 2px 4px rgba(0, 102, 255, 0.08), 0 1px 2px rgba(138, 43, 226, 0.06)',
      // 标准阴影 - 层次感
      medium: '0 4px 8px rgba(0, 102, 255, 0.12), 0 2px 4px rgba(138, 43, 226, 0.08)',
      // 深度阴影 - 高端感
      heavy: '0 8px 16px rgba(0, 102, 255, 0.16), 0 4px 8px rgba(138, 43, 226, 0.12)',
      // 极深阴影 - 奢华感
      extreme: '0 16px 32px rgba(0, 102, 255, 0.20), 0 8px 16px rgba(138, 43, 226, 0.16)',
    },
    // ERC-721 专属彩色阴影
    colored: {
      // 主色阴影 - 蓝色科技感
      primary: '0 6px 20px 0 rgba(0, 102, 255, 0.35)',
      // 次色阴影 - 紫色神秘感
      secondary: '0 6px 20px 0 rgba(138, 43, 226, 0.35)',
      // 金色阴影 - 奢华感
      gold: '0 6px 20px 0 rgba(255, 215, 0, 0.40)',
      // 成功色阴影 - 绿色增值感
      success: '0 6px 20px 0 rgba(76, 175, 80, 0.35)',
      // 警告色阴影 - 橙色注意感
      warning: '0 6px 20px 0 rgba(255, 152, 0, 0.35)',
      // 错误色阴影 - 红色警示感
      error: '0 6px 20px 0 rgba(244, 67, 54, 0.35)',
      // 信息色阴影 - 蓝色信息感
      info: '0 6px 20px 0 rgba(33, 150, 243, 0.35)',
      // 稀有度阴影 - 彩虹渐变
      rare: '0 8px 24px 0 rgba(255, 215, 0, 0.25), 0 4px 12px 0 rgba(138, 43, 226, 0.25)',
    },
  },
};

const shadows = {
  light: createShadow(LIGHT_MODE.primary),
  dark: createShadow(DARK_MODE.primary),
};

export {
  createShadow,
  createCustomShadow,
  createColoredShadow,
  createMultiAssetShadow,
  LIGHT_MODE,
  DARK_MODE,
  MULTI_ASSET_SHADOWS,
  PREMIUM_SHADOWS // Legacy ERC-721 compatibility
};

export default shadows;
